import { DealType } from '@ca/common/types'
import { BrandOptionSchema } from 'features/config/ConfigConditionSchemas'
import { useConfigConditionValue } from 'features/config/useConfigConditionValue'
import { useCardFilter } from '@screens/intro/deals/useCardFilter'
import { useAllowedDealTypes } from './useDeals'

export type BrandFilterOption = {
  id: DealType
  label: string
}

export type BrandFilterResult =
  | {
      brandOptions: BrandFilterOption[]
      activeBrandOption: BrandFilterOption | undefined
      setBrand: (id: DealType) => void
      brandRequiredTags: undefined
      brandExcludedTags: undefined
      priorityTags: undefined
      isTagMode: false
    }
  | {
      brandOptions: BrandFilterOption[]
      activeBrandOption: BrandFilterOption | undefined
      setBrand: (id: DealType) => void
      brandRequiredTags: string[] | undefined
      brandExcludedTags: string[] | undefined
      priorityTags: string[][] | undefined
      isTagMode: true
    }

export const useBrandFilter = (opts?: { types?: DealType[] }): BrandFilterResult => {
  const allowedDealTypes = useAllowedDealTypes()
  const brandsConfig = useConfigConditionValue('intro:brands', 'brands')
  const { type, setType } = useCardFilter()

  const effectiveDealTypes = opts?.types ?? allowedDealTypes
  const isTagMode = brandsConfig != null

  if (!isTagMode) {
    const brandOptions = effectiveDealTypes.map((dt) => ({ id: dt, label: dt }))
    const activeBrandOption = brandOptions.find((o) => o.id === type) ?? brandOptions[0]
    return {
      brandOptions,
      activeBrandOption,
      setBrand: (id: DealType) => setType(id),
      brandRequiredTags: undefined,
      brandExcludedTags: undefined,
      priorityTags: undefined,
      isTagMode: false,
    }
  }

  const options: BrandOptionSchema[] = brandsConfig.options.filter((o) =>
    effectiveDealTypes.includes(o.name)
  )
  const brandOptions = options.map((o) => ({ id: o.name, label: o.name }))

  const activeBrandConfig = options.find((o) => o.name === type) ?? options[0]
  const activeBrandOption = activeBrandConfig
    ? { id: activeBrandConfig.name, label: activeBrandConfig.name }
    : brandOptions[0]

  return {
    brandOptions,
    activeBrandOption,
    setBrand: (id: DealType) => setType(id),
    brandRequiredTags: activeBrandConfig?.requiredTags?.length
      ? activeBrandConfig.requiredTags
      : undefined,
    brandExcludedTags: activeBrandConfig?.excludedTags?.length
      ? activeBrandConfig.excludedTags
      : undefined,
    priorityTags: activeBrandConfig?.priorityTags ?? undefined,
    isTagMode: true,
  }
}
